// client/src/components/secure/PortalAssistantPanel.js — "Ask the Assistant" inside the portal.
//
// One panel for both tiers (routes/chatAdmin.js assistantTier):
//   staff  lib/staffAssistant — office staff, office manager, managing broker and agents; it
//          answers from what the person may already see and can PROPOSE a change (a task for
//          them, ticking a TC step) which waits here for Confirm / Dismiss
//   agent  lib/agentAssistant — the older agent-only tier while the staff switch is off
// Nothing changes until the person presses Confirm; the server re-checks access then.
import React, { useCallback, useEffect, useState } from 'react';

const API = '/api/chat-admin/my/assistant';
const card = 'bg-white rounded-lg shadow p-4';
const btn = 'px-3 py-1.5 rounded text-sm';

const SUGGESTIONS = {
    staff: ['What is overdue on my deals and TC items this week?', 'Which showings are coming up?', 'Any new leads in the last 7 days?', 'How do I reconcile the escrow account?'],
    agent: ['What are my follow-ups and deadlines for today?', 'Write a listing description for my newest listing', 'How do I log a showing?'],
};

function Proposal({ p, onDecide, busy }) {
    const done = p.status && p.status !== 'pending';
    return (
        <div className={`mt-2 p-2 rounded border text-sm ${done ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-300'}`}>
            <div><b>Proposed change:</b> {p.summary}</div>
            {done ? <div className="text-xs mt-1 text-slate-600">{p.status === 'confirmed' ? `Done. ${p.message || ''}` : p.status === 'dismissed' ? 'Dismissed — nothing was changed.' : p.message || p.status}</div>
                : (
                    <div className="flex gap-2 mt-2">
                        <button type="button" disabled={busy} className={`${btn} bg-emerald-700 text-white disabled:opacity-50`} onClick={() => onDecide(p, 'confirm')}>Confirm</button>
                        <button type="button" disabled={busy} className={`${btn} border`} onClick={() => onDecide(p, 'dismiss')}>Dismiss</button>
                        <span className="text-xs text-slate-500 self-center">Nothing happens until you confirm.</span>
                    </div>
                )}
        </div>
    );
}

export default function PortalAssistantPanel({ api }) {
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);   // { role, content, proposals? }
    const [pending, setPending] = useState([]);
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const loadPending = useCallback(() => {
        api.get(`${API}/proposals`).then((rows) => setPending(Array.isArray(rows) ? rows : [])).catch(() => setPending([]));
    }, [api]);
    useEffect(() => {
        api.get(`${API}/status`).then((s) => { setStatus(s); if (s.tier === 'staff') loadPending(); })
            .catch((e) => setStatus({ available: false, reason: e.message }));
    }, [api, loadPending]);

    const ask = async (e) => {
        e.preventDefault();
        const message = q.trim();
        if (!message) return;
        setBusy(true); setErr('');
        try {
            const plain = history.map(({ role, content }) => ({ role, content }));
            const r = await api.post(API, { message, history: plain });
            const warn = (r.fair_housing_warnings || []).map((w) => `• "${w.term}" — ${w.why} ${w.suggestion}`).join('\n');
            setHistory([...history, { role: 'user', content: message },
                { role: 'assistant', content: warn ? `${r.reply}\n\nFair-housing check:\n${warn}` : r.reply, proposals: (r.proposals || []).map((p) => ({ ...p, status: 'pending' })) }]);
            setQ('');
            if (r.tier === 'staff') loadPending();
        } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
    };

    const decide = async (p, decision) => {
        setBusy(true); setErr('');
        let patch;
        try {
            const r = await api.post(`${API}/proposals/${p.proposal_id}/${decision}`, {});
            patch = { status: r.status, message: r.message };
        } catch (e2) { patch = { status: 'failed', message: e2.message }; setErr(e2.message); }
        setHistory((h) => h.map((m) => (m.proposals ? { ...m, proposals: m.proposals.map((x) => (x.proposal_id === p.proposal_id ? { ...x, ...patch } : x)) } : m)));
        setBusy(false);
        loadPending();
    };

    if (!status) return <div className={card}><p className="text-sm text-slate-500">Loading…</p></div>;
    if (!status.available) return <div className={card}><p className="text-sm text-slate-600">{status.reason || 'The assistant is not available to you.'}</p></div>;
    const staffTier = status.tier === 'staff';
    const shown = new Set(history.flatMap((m) => (m.proposals || []).map((p) => p.proposal_id)));
    const older = pending.filter((p) => !shown.has(p.proposal_id));

    return (
        <div className={`${card} space-y-3`}>
            <p className="text-xs text-slate-500">
                {staffTier
                    ? `Answering as: ${status.role}. Ask about deals, TC items, showings, leads, campaigns, CMAs, property management or how to do something in the portal. It only sees what you may already see, it cannot change anything itself — a task or a TC step it suggests waits below for you to Confirm — and every question is logged with its cost.`
                    : 'Ask about your follow-ups and deadlines, a client\'s history, a listing description, or how to do something in the portal. It sees only your own book, cannot send anything (drafts go to My outbox for you to send), checks listing copy for fair-housing problems, and every action is logged.'}
            </p>
            {older.length > 0 && (
                <div>
                    <div className="text-xs font-medium text-slate-600">Waiting for your decision</div>
                    {older.map((p) => <Proposal key={p.proposal_id} p={{ ...p, status: 'pending' }} busy={busy} onDecide={decide} />)}
                </div>
            )}
            <div className="flex flex-wrap gap-2 text-xs">
                {SUGGESTIONS[staffTier ? 'staff' : 'agent'].map((s) => <button type="button" key={s} className="px-2 py-1 border rounded-full" onClick={() => setQ(s)}>{s}</button>)}
            </div>
            <ul className="space-y-2 max-h-[32rem] overflow-y-auto" aria-live="polite">
                {history.map((m, i) => (
                    <li key={i} className={`text-sm whitespace-pre-line rounded p-2 ${m.role === 'user' ? 'bg-indigo-50' : 'bg-slate-50'}`}>
                        <b>{m.role === 'user' ? 'You' : 'Assistant'}:</b> {m.content}
                        {(m.proposals || []).map((p) => <Proposal key={p.proposal_id} p={p} busy={busy} onDecide={decide} />)}
                    </li>
                ))}
            </ul>
            {err && <p className="text-sm text-red-600" role="alert">{err}</p>}
            <form onSubmit={ask} className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={2000} className="flex-1 p-2 border rounded text-sm" placeholder="Ask the assistant…" aria-label="Ask the assistant" />
                <button disabled={busy} className={`${btn} bg-slate-800 text-white disabled:bg-indigo-300`}>{busy ? 'Thinking…' : 'Ask'}</button>
            </form>
        </div>
    );
}
